/**
 * Agent API — chat, notifications, AI settings.
 */

import client from './client';

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

/** Send a message. Creates session if session_id is null. */
export async function sendChatMessage({ sessionId, message }) {
  const { data } = await client.post('/agent/chat/', {
    session_id: sessionId,
    message,
  });
  return data;
}

/** Load a session with full message history. */
export async function getChatSession(sessionId) {
  const { data } = await client.get(`/agent/chat/${sessionId}/`);
  return data.data;
}

/** List recent sessions. */
export async function getChatSessions() {
  const { data } = await client.get('/agent/sessions/');
  return data.data || [];
}

/** Execute a proposed action from a chat message. */
export async function executeProposedAction(messageId) {
  const { data } = await client.post(`/agent/execute/${messageId}/`);
  return data;
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

/** Fetch notifications. */
export async function getNotifications({ unreadOnly = false, severity = null, limit = 50 } = {}) {
  const params = { limit };
  if (unreadOnly) params.unread_only = 'true';
  if (severity) params.severity = severity;
  const { data } = await client.get('/agent/notifications/', { params });
  return data;
}

/** Get unread count (lightweight — for badge polling). */
export async function getUnreadCount() {
  const { data } = await client.get('/agent/notifications/unread-count/');
  return data.count || 0;
}

/** Mark a single notification as read. */
export async function markNotificationRead(id) {
  await client.post(`/agent/notifications/${id}/read/`);
}

/** Mark all notifications as read. */
export async function markAllNotificationsRead() {
  await client.post('/agent/notifications/mark-all-read/');
}

// ---------------------------------------------------------------------------
// AI Settings
// ---------------------------------------------------------------------------

/** Get AI settings. */
export async function getAiSettings() {
  const { data } = await client.get('/agent/settings/');
  return data.data || {};
}

/** Update AI settings (admin only). */
export async function updateAiSettings(settings) {
  const { data } = await client.post('/agent/settings/update/', settings);
  return data;
}

/** Manually trigger a monitor run (admin only). */
export async function triggerMonitor() {
  const { data } = await client.post('/agent/monitor/run/');
  return data;
}

// ---------------------------------------------------------------------------
// Daily Report
// ---------------------------------------------------------------------------

/** Generate a fresh daily report. */
export async function generateDailyReport() {
  const { data } = await client.post('/agent/daily-report/generate/');
  return data.data;
}

/** Get the most recent report. */
export async function getLatestDailyReport() {
  const { data } = await client.get('/agent/daily-report/latest/');
  return data.data;
}

/** Check if there's an unread report. */
export async function hasNewDailyReport() {
  const { data } = await client.get('/agent/daily-report/has-new/');
  return data.data?.has_new || false;
}

/** Dismiss a report. */
export async function dismissDailyReport(reportId) {
  const { data } = await client.post(`/agent/daily-report/${reportId}/dismiss/`);
  return data.data;
}

/** Mark report as read. */
export async function markDailyReportRead(reportId) {
  const { data } = await client.post(`/agent/daily-report/${reportId}/read/`);
  return data.data;
}

/** Submit instruction for a report item (preview only). */
export async function instructReportItem(itemId, instruction) {
  const { data } = await client.post(`/agent/daily-report/items/${itemId}/instruct/`, { instruction });
  return data.data;
}

/** Execute a confirmed instruction. */
export async function executeReportItem(itemId) {
  const { data } = await client.post(`/agent/daily-report/items/${itemId}/execute/`);
  return data.data;
}

/** Snooze a report item. */
export async function snoozeReportItem(itemId, days = 1) {
  const { data } = await client.post(`/agent/daily-report/items/${itemId}/snooze/`, { days });
  return data.data;
}

/** Get report history. */
export async function getDailyReportHistory() {
  const { data } = await client.get('/agent/daily-report/history/');
  return data.data || [];
}
